import parse from 'html-react-parser';

let DOMPurify = null;
try {
    // eslint-disable-next-line global-require
    const createDOMPurify = require('isomorphic-dompurify');
    DOMPurify = createDOMPurify();
} catch (_) {
    DOMPurify = null;
}

export function sanitizeHtml(html = '', options = {}) {
    if (typeof html !== 'string' || html.length === 0) return '';
    // Normalize common non-breaking space encodings that may come from CMS (AEM)
    // Replace HTML entity &nbsp; and the actual U+00A0 character with a normal space
    // This prevents large gaps caused by non-breaking spaces in rendered text.
    let normalized = html.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
    // Normalize <br> usage:
    // - Remove empty paragraphs that only contain a <br>
    // - Convert <br> to newlines so CSS white-space: pre-line handles them
    // - Collapse multiple <br>s (or resulting newlines) to a single newline
    normalized = normalized
        .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
        // Replace runs of 2+ <br> with two newlines to indicate a paragraph break
        .replace(/(<br\s*\/?>\s*){2,}/gi, '\n\n')
        // Single <br> becomes a single newline
        .replace(/<br\s*\/?>/gi, '\n');

    if (DOMPurify && typeof DOMPurify.sanitize === 'function') {
        const sanitized = DOMPurify.sanitize(normalized, options);
        // Ensure sanitized output doesn't contain any remaining NBSP chars
        if (typeof sanitized === 'string') {
            return sanitized
                .replace(/\u00A0/g, ' ')
                // Collapse 3+ newlines down to exactly two (paragraph break)
                .replace(/\n{3,}/g, '\n\n')
                .replace(/^[\s\n]+|[\s\n]+$/g, '');
        }
        return sanitized;
    }

    return normalized
        .replace(/\n{3,}/g, '\n\n')
        .replace(/^[\s\n]+|[\s\n]+$/g, '');
}

export function parseSafe(html, parseOptions = {}, purifyOptions = {}) {
    return parse(sanitizeHtml(html, purifyOptions), parseOptions);
}

export function sanitizeUrl(url = '', allowedDomains = ['headless.expediagroup.com']) {
    if (typeof url !== 'string' || url.length === 0) return '';
    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) return '';

    const base = 'https://headless.expediagroup.com';
    try {
        if (trimmedUrl.startsWith('/')) {
            return `${base}${trimmedUrl}`;
        }
        const parsedUrl = new URL(trimmedUrl, base);
        if (parsedUrl.href.startsWith(base + base)) {
            return parsedUrl.href.replace(base + base, base);
        }
        if (!allowedDomains.includes(parsedUrl.hostname)) {
            return '';
        }
        return parsedUrl.href;
    } catch (error) {
        return '';
    }
}

// Very small URL sanitizer: allow http(s), absolute-paths ('/...'), and in-page anchors;
// otherwise fallback to '#'
export function getSafeHref(url) {
    if (typeof url !== 'string' || url.trim() === '') return '#';
    const trimmed = url.trim();
    if (trimmed.startsWith('#')) return trimmed; // in-page anchors
    if (trimmed.startsWith('/')) return trimmed; // absolute-path links within site
    const lowered = trimmed.toLowerCase();
    if (lowered.startsWith('http://') || lowered.startsWith('https://')) return trimmed;
    return '#';
}

export default {
    sanitizeHtml,
    parseSafe,
    sanitizeUrl,
    getSafeHref
};
